import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../api';
import type { User } from '@supabase/supabase-js';
import type { UserProfile } from '../types';

/** 未登录时 Supabase 会报此错误，属正常状态，不按错误打印 */
function isSessionMissingError(error: unknown): boolean {
  return (error as { name?: string })?.name === 'AuthSessionMissingError';
}

export function useAuthUser() {
  return useQuery({
    queryKey: ['auth', 'user'],
    queryFn: async (): Promise<User | null> => {
      if (!supabase) return null;
      try {
        const { data, error } = await supabase.auth.getUser();
        if (error) {
          if (!isSessionMissingError(error)) {
            console.error('[useAuthUser] 获取用户失败:', error);
          }
          return null;
        }
        return data.user;
      } catch (error) {
        if (!isSessionMissingError(error)) {
          console.error('[useAuthUser] 获取用户异常:', error);
        }
        return null;
      }
    },
    staleTime: Infinity,
    gcTime: 10 * 60 * 1000,
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchInterval: false,
  });
}

export function useUserProfile(userId: string | null | undefined) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ['auth', 'profile', userId],
    queryFn: async (): Promise<UserProfile | null> => {
      if (!supabase || !userId) return null;
      try {
        const cachedUser = queryClient.getQueryData<User | null>(['auth', 'user']);
        const userEmail = cachedUser?.email || '';
        if (!cachedUser) return null;

        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .single();

        if (error) {
          console.error('[useUserProfile] 获取 profile 失败:', error);
          return null;
        }
        if (!data) return null;

        return {
          id: data.id,
          username: data.username || '',
          email: userEmail,
          created_at: data.created_at,
          updated_at: data.updated_at,
        };
      } catch (error) {
        console.error('[useUserProfile] 获取 profile 异常:', error);
        return null;
      }
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
    retryDelay: 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
}

export function useSignInMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ email, password }: { email: string; password: string }) => {
      if (!supabase) throw new Error('Supabase 未配置');
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw new Error(error.message);
      return data.user;
    },
    onSuccess: (user) => {
      queryClient.setQueryData(['auth', 'user'], user);
      if (user) {
        queryClient.invalidateQueries({ queryKey: ['auth', 'profile', user.id] });
      }
    },
  });
}

export function useSignUpMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      email,
      password,
      username,
    }: {
      email: string;
      password: string;
      username?: string;
    }) => {
      if (!supabase) throw new Error('Supabase 未配置');
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: { username: username || email.split('@')[0] } },
      });
      if (error) throw new Error(error.message);

      if (data.user && username) {
        await supabase
          .from('profiles')
          .upsert({ id: data.user.id, username, email }, { onConflict: 'id' });
      }
      return data.user;
    },
    onSuccess: (user) => {
      queryClient.setQueryData(['auth', 'user'], user);
      if (user) {
        queryClient.invalidateQueries({ queryKey: ['auth', 'profile', user.id] });
      }
    },
  });
}

export function useSignOutMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      if (!supabase) return;
      const { error } = await supabase.auth.signOut();
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      queryClient.setQueryData(['auth', 'user'], null);
      queryClient.removeQueries({ queryKey: ['auth', 'profile'] });
    },
  });
}
